import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonProps } from "../Button";
import { useCallback } from "react";
import { ModifyEvent } from "ol/interaction/Modify";
import { useFeatureStore, useMap } from "../../../hooks";
import { useControlSection } from "../../layout";
import { InnerButton } from "../InnerButton";
import { useFeaturesStore } from "../../../FeaturesStore";
import { ModifyIcon } from "../../../constants/icons/ModifyIcon";
import { useFeatureFinder } from "../../../hooks/incontext/useFeatureFinder";
import { useModifyInteraction } from "../../../hooks/incontext/useModifyInteraction";
import { useMapInteractionLock } from "../../../hooks/incontext/useMapInteractionLock";
import { useModifyHandleSync } from "../../../hooks/incontext/useModifyHandleSync";
import { useReleaseToolWithoutSelection } from "src/domains/shared/react-openlayers/Map/hooks/incontext/useReleaseToolWithoutSelection";
import { setMapAppState } from "../../../utils/mapState";
import { resolveModifiedFeatureUpdate } from "../../../utils/geometry";

import { MAP_TOOL_BUTTON } from "src/domains/shared/constants/mapToolButton";

export interface ModifyAnnotationProps extends ButtonProps {
  onModifyChange?: (e: ModifyEvent) => void;
  target: any | null;
}

export function ModifyAnnotation({
  onModifyChange,
  target,
  disabled,
  ...props
}: ModifyAnnotationProps) {
  const { t } = useTranslation("timeSeries");
  const { selectedFeature } = useFeatureStore();
  const { geoJson, updateGeoJson, getGeoJsonElement } = useFeaturesStore();
  const map = useMap();
  const id = useId();
  const buttonId = `controlbutton-${id}`;
  const { selectButton, selectedButtonId } = useControlSection();
  const isActive = buttonId === selectedButtonId;
  const { findFeatureById } = useFeatureFinder();

  const onModifyStart = useCallback(
    () => setMapAppState(map, { isModifying: true }),
    [map]
  );

  const onModifyEnd = useCallback(
    (event: ModifyEvent) => {
      const result = resolveModifiedFeatureUpdate(event, geoJson, getGeoJsonElement, isActive);
      if (result) {
        updateGeoJson(result.id, result.data);
        onModifyChange?.(event);
      }
    },
    [geoJson, getGeoJsonElement, updateGeoJson, isActive, onModifyChange]
  );

  const { install: installModify, remove: removeModify, getInstalledFeatureId } =
    useModifyInteraction(map, { onStart: onModifyStart, onEnd: onModifyEnd });

  useMapInteractionLock(map, isActive);

  useReleaseToolWithoutSelection({
    isActive,
    hasSelection: Boolean(selectedFeature?.id),
    release: () => selectButton(""),
  });

  useModifyHandleSync({
    isActive,
    map,
    geoJson,
    selectedFeature,
    findFeatureById,
    installModify,
    removeModify,
    getInstalledFeatureId,
    updateGeoJson,
  });

  const handleButtonClick = useCallback(() => {
    const fid = selectedFeature?.id ? String(selectedFeature.id) : null;

    if (isActive) {
      if (fid) {
        const cur = getGeoJsonElement(fid);
        if (cur) updateGeoJson(fid, { ...cur, properties: { ...cur.properties, isModifying: false } });
      }
      selectButton("");
    } else {
      if (!fid) return;
      selectButton(buttonId);
      const cur = getGeoJsonElement(fid);
      if (cur) updateGeoJson(fid, { ...cur, properties: { ...cur.properties, isModifying: true } });
    }
  }, [isActive, selectedFeature, getGeoJsonElement, updateGeoJson, selectButton, buttonId]);

  const isButtonDisabled = disabled || !selectedFeature?.id;

  return (
    <Button
      id={buttonId}
      isDisabled={isButtonDisabled}
      disabled={isButtonDisabled}
      hasPopup
      popupText={t("mapTool.tool.modify")}
      onClick={handleButtonClick}
      isActive={isActive}
      aria-pressed={isActive}
      {...props}
    >
      <InnerButton isActive={isActive}>
        <ModifyIcon
          size={MAP_TOOL_BUTTON.ICON_PX}
          color={isActive ? MAP_TOOL_BUTTON.activeIcon : MAP_TOOL_BUTTON.idleIcon}
        />
      </InnerButton>
    </Button>
  );
}
